import { useEffect, useState } from "react";
import { Divider, Modal, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { useCheckPermission } from "../../../../../hooks/useCheckPermission";
import { useRole } from "../../../../../redux/features/user";
import {
  getCurrentRuleVersion,
  getPreviousVersionDate,
} from "../../../../../utils/functions";

import { useRuleQuery } from "../../../../../components/QueryBuilder/hooks/useRuleQuery";
import { useRuleHistoryQuery } from "./useRuleHistoryQuery";
import ActivateDeactivateSection from "./ActivateDeactivateSection";
import CloneSection from "./CloneSection";
import RuleHistoryTable from "./RuleHistoryTable";

/**
 * The Details modal for a rule: a permission-gated Activate/Deactivate
 * section, a role-gated (ADMIN / GRANT_ADMIN) Clone section, and the
 * rule change-history table.
 *
 * History is fetched lazily via useRuleHistoryQuery — only while the
 * modal is open. The previous-version date used as the min for the
 * activate date-picker is derived from that history.
 */
export default function RuleDetailsPopup({ ruleId, opened, onClose }) {
  const { t } = useTranslation();
  const [openedSection, setOpenedSection] = useState(null); // 'activate' | 'clone' | null

  const role = useRole();
  const activatePermission = useCheckPermission("ACTIVATE_RULE");
  const deactivatePermission = useCheckPermission("DEACTIVATE_RULE");
  const clonePermission = role === "ADMIN" || role === "GRANT_ADMIN";

  const { data: rule } = useRuleQuery(ruleId);
  const { data: history, error: historyError } = useRuleHistoryQuery({
    ruleId,
    enabled: opened,
  });

  useEffect(() => {
    if (opened && historyError) toast.error("error");
  }, [opened, historyError]);

  useEffect(() => {
    if (!opened) setOpenedSection(null);
  }, [opened]);

  const active = rule?.status === "ACTIVATED";
  const currentRuleVersion = getCurrentRuleVersion(history, rule?.id);
  const previousVersionDateRaw = getPreviousVersionDate(
    history,
    currentRuleVersion,
  );
  const previousVersionDate = previousVersionDateRaw
    ? new Date(previousVersionDateRaw)
    : null;

  const handleSectionToggle = (section) =>
    setOpenedSection((prev) => (prev === section ? null : section));

  const handleSectionClose = () => setOpenedSection(null);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Details"
      size={1300}
      centered
    >
      <Stack gap="md">
        {activatePermission && active && (
          <ActivateDeactivateSection
            ruleId={ruleId}
            isActive
            {...rule}
            expanded={openedSection === "activate"}
            onToggle={() => handleSectionToggle("activate")}
            previousVersionDate={previousVersionDate}
            onSuccess={handleSectionClose}
          />
        )}
        {deactivatePermission && !active && (
          <ActivateDeactivateSection
            ruleId={ruleId}
            isActive={false}
            {...rule}
            expanded={openedSection === "activate"}
            onToggle={() => handleSectionToggle("activate")}
            previousVersionDate={previousVersionDate}
            onSuccess={handleSectionClose}
          />
        )}
        {clonePermission && (
          <CloneSection
            ruleId={ruleId}
            expanded={openedSection === "clone"}
            onToggle={() => handleSectionToggle("clone")}
            onSuccess={handleSectionClose}
          />
        )}

        <Divider />

        <Text fw={500} ta="center">
          {t("multipleSearch.detailsPopup.changeHistory")}
        </Text>
        <RuleHistoryTable history={history} currentRuleId={rule?.id} />
      </Stack>
    </Modal>
  );
}
